import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, extractError } from "../lib/api";
import { toast } from "sonner";
import Logo from "./Logo";
import {
  Users,
  BookOpenText,
  ChartBar,
  SignOut,
  Scales,
  DownloadSimple,
  Heartbeat,
  Gear,
  IdentificationCard,
  Buildings,
  TestTube,
} from "@phosphor-icons/react";

const NAV_ITEMS = [
  { to: "/admin/users", label: "Benutzer", icon: Users },
  { to: "/admin/kvs", label: "Kollektivverträge", icon: BookOpenText },
  { to: "/admin/sv-codes", label: "SV-Codes", icon: IdentificationCard },
  { to: "/admin/statistik", label: "Statistik", icon: ChartBar },
  { to: "/admin/systemstatus", label: "Systemstatus", icon: Heartbeat },
];

// Iter 19.52 — nur für Superadmins sichtbar.
// Iter 19.68.17 — „Unternehmen" (Company-Verwaltung + Kontingente) ist
// bewusst Superadmin-only, weil Quoten die Abrechnung beeinflussen.
// Iter 20.36.f10 — „Testsuite" (Persona-Library) ist ebenfalls superadmin-only.
const SUPERADMIN_NAV_ITEMS = [
  { to: "/admin/companies", label: "Unternehmen", icon: Buildings },
  { to: "/admin/testsuite", label: "Testsuite", icon: TestTube },
  { to: "/admin/system-einstellungen", label: "System-Einstellungen", icon: Gear },
];

export default function AdminLayout({ children, title, subtitle, actions }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [downloading, setDownloading] = React.useState(false);

  // Iter 19.52 — Superadmin-only entries appended (System-Einstellungen).
  const navItems = React.useMemo(() => {
    const items = [...NAV_ITEMS];
    if (user?.role === "superadmin") {
      items.push(...SUPERADMIN_NAV_ITEMS);
    }
    return items;
  }, [user?.role]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleBackupDownload = async () => {
    setDownloading(true);
    try {
      const resp = await api.get("/admin/backup/download", {
        responseType: "blob",
        timeout: 60_000,
      });
      const blob = new Blob([resp.data], { type: "application/json" });
      const cd = resp.headers?.["content-disposition"] || "";
      const m = /filename="([^"]+)"/i.exec(cd);
      const fname = m ? m[1] : `tari-backup-${new Date().toISOString().slice(0, 10)}.json`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Backup erstellt: ${fname}`);
    } catch (err) {
      let msg = "Backup-Download fehlgeschlagen.";
      try {
        const blob = err?.response?.data;
        if (blob && blob.text) {
          const text = await blob.text();
          const json = JSON.parse(text);
          if (json.detail) msg = json.detail;
        } else {
          msg = extractError(err, msg);
        }
      } catch {
        msg = extractError(err, msg);
      }
      toast.error(msg);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink-50 flex" data-testid="admin-layout">
      {/* SIDEBAR */}
      <aside className="hidden lg:flex flex-col w-72 shrink-0 bg-white border-r border-ink-200 sticky top-0 h-screen">
        <div className="px-6 py-5 border-b border-ink-200">
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="block text-left transition-crisp hover:opacity-80"
            data-testid="admin-sidebar-logo"
            title="Zur Einstufung"
          >
            <Logo size="md" />
          </button>
        </div>

        <div className="px-3 pt-4 pb-2">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 px-3 mb-2">
            Administration
          </div>
          <nav className="flex flex-col gap-1" data-testid="admin-sidebar-nav">
            {navItems.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                end
                data-testid={`admin-nav-${to.split("/").pop()}`}
                className={({ isActive }) =>
                  [
                    "group flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium transition-crisp",
                    isActive
                      ? "bg-ink-900 text-white"
                      : "text-warm-700 hover:bg-ink-100 hover:text-ink-900",
                  ].join(" ")
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon
                      size={16}
                      weight={isActive ? "bold" : "regular"}
                      className={isActive ? "text-brand-400" : "text-warm-500 group-hover:text-ink-900"}
                    />
                    {label}
                  </>
                )}
              </NavLink>
            ))}
          </nav>
        </div>

        <div className="mt-auto border-t border-ink-200 p-4 space-y-3">
          <div className="px-1">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-1">
              Angemeldet als
            </div>
            <div className="font-medium text-ink-900 text-sm truncate">
              {user?.name}
            </div>
            <div className="text-xs text-warm-600 truncate">{user?.email}</div>
          </div>
          <div className="flex flex-col gap-1.5">
            <button
              onClick={handleBackupDownload}
              disabled={downloading}
              data-testid="admin-sidebar-backup"
              title="Aktuellen Stand aller User + KVs als JSON herunterladen"
              className="inline-flex items-center gap-2 rounded-sm border border-brand-300 bg-brand-50 px-3 py-1.5 text-xs font-medium text-brand-700 transition-crisp hover:bg-brand-100 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <DownloadSimple size={13} weight="bold" />
              {downloading ? "Backup läuft…" : "Backup herunterladen"}
            </button>
            <button
              onClick={() => navigate("/dashboard")}
              data-testid="admin-sidebar-tool"
              className="inline-flex items-center gap-2 rounded-sm border border-ink-300 bg-white px-3 py-1.5 text-xs font-medium text-ink-700 transition-crisp hover:bg-ink-100"
            >
              <Scales size={13} weight="bold" />
              Zum Einstufungstool
            </button>
            <button
              onClick={handleLogout}
              data-testid="admin-sidebar-logout"
              className="inline-flex items-center gap-2 rounded-sm bg-ink-900 px-3 py-1.5 text-xs font-medium text-white transition-crisp hover:bg-ink-800"
            >
              <SignOut size={13} weight="bold" />
              Abmelden
            </button>
          </div>
        </div>
      </aside>

      {/* MAIN */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top-bar with sidebar fallback */}
        <div className="lg:hidden border-b border-ink-200 bg-white px-6 h-16 flex items-center justify-between sticky top-0 z-30">
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="inline-flex transition-crisp hover:opacity-80"
            data-testid="admin-mobile-logo"
            title="Zur Einstufung"
          >
            <Logo size="sm" />
          </button>
          <button
            onClick={handleLogout}
            data-testid="admin-mobile-logout"
            className="inline-flex items-center gap-1.5 rounded-sm border border-ink-300 px-3 py-1.5 text-xs font-medium text-ink-700"
          >
            <SignOut size={13} weight="bold" />
            Abmelden
          </button>
        </div>

        {/* Mobile nav strip */}
        <div className="lg:hidden border-b border-ink-200 bg-white px-4 py-2 flex gap-1 overflow-x-auto sticky top-16 z-20">
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end
              className={({ isActive }) =>
                [
                  "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-medium whitespace-nowrap transition-crisp",
                  isActive ? "bg-ink-900 text-white" : "text-warm-600 hover:bg-ink-100",
                ].join(" ")
              }
            >
              <Icon size={14} weight="bold" />
              {label}
            </NavLink>
          ))}
        </div>

        {/* Page header */}
        <header className="border-b border-ink-200 bg-white">
          <div className="px-6 lg:px-10 py-8 flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest font-semibold text-warm-500 mb-2">
                Administration
              </div>
              <h1
                className="font-cabinet text-3xl sm:text-4xl tracking-tight text-ink-900 font-medium leading-none"
                data-testid="admin-page-title"
              >
                {title}
              </h1>
              {subtitle && (
                <p className="mt-3 max-w-2xl text-sm text-warm-700">{subtitle}</p>
              )}
            </div>
            {actions && <div className="flex items-center gap-2">{actions}</div>}
          </div>
        </header>

        {/* Main content */}
        <main className="flex-1 px-6 lg:px-10 py-8">{children}</main>
      </div>
    </div>
  );
}
